import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchWithTimeout,
  isNetworkError,
  NetworkError,
  getNetworkErrorMessage,
  TimeoutError,
  getTimeoutErrorMessage
} from '../utils/networkError';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setResetLink('');

    // Validate email
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/auth/forgot-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email })
        },
        15000 // 15 second timeout
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.errors?.[0]?.msg || 'Failed to send reset email');
      }

      setSuccess(true);

      // In development mode, backend returns the reset link
      if (data.resetLink) {
        setResetLink(data.resetLink);
      }
    } catch (err) {
      console.error('Forgot password error:', err);

      if (err instanceof TimeoutError) {
        setError(getTimeoutErrorMessage());
      } else if (isNetworkError(err)) {
        setError(getNetworkErrorMessage());
      } else {
        setError(err.message || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Forgot Password
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Form */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-8">
          {success ? (
            <div className="text-center">
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400">
                <p className="font-body">
                  If an account with that email exists, a password reset link has been sent.
                </p>
                <p className="mt-2 text-sm text-green-400/80">
                  Please check your email (including spam folder) and follow the instructions.
                </p>
              </div>

              {/* Development mode: show reset link */}
              {resetLink && (
                <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <p className="font-mono text-xs uppercase tracking-widest text-blue-400 mb-2">
                    Development Mode - Reset Link:
                  </p>
                  <a
                    href={resetLink}
                    className="text-sm text-blue-400 hover:text-blue-300 break-all underline"
                  >
                    {resetLink}
                  </a>
                </div>
              )}

              <Link
                to="/login"
                className="btn-primary inline-block"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field"
                    placeholder="john.doe@example.com"
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              {/* Back to Login */}
              <p className="mt-6 text-center text-light-text-secondary dark:text-silver/60 font-body text-sm">
                Remember your password?{' '}
                <Link
                  to="/login"
                  className="text-light-text dark:text-pure-white hover:underline transition-smooth"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
